<template>
  <div>
    <!-- Landing: menu of settings sections -->
    <div v-if="!activeSection">
      <h4 class="table-page-header">Settings</h4>
      <div class="settings-menu">
        <router-link
          v-for="s in sections"
          :key="s.id"
          :to="`/pages/settings/${s.id}`"
          class="settings-menu-card"
        >
          <i :class="s.icon" class="settings-menu-icon"></i>
          <span class="settings-menu-text">
            <span class="settings-menu-title">{{ s.title }}</span>
            <span class="settings-menu-desc">{{ s.desc }}</span>
          </span>
          <i class="fa-solid fa-chevron-right settings-menu-chevron"></i>
        </router-link>
      </div>
    </div>

    <!-- Section views: back link to the menu -->
    <router-link v-else to="/pages/settings" class="settings-back-link">
      <i class="fa-solid fa-arrow-left"></i>
      All Settings
    </router-link>

    <div v-if="activeSection === 'service-codes'">
      <h5 class="table-page-header">
        Service Codes
        <el-tooltip content="Add New Service Code">
          <el-button type="text" @click="createServiceCode">
            <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
          </el-button>
        </el-tooltip>
      </h5>
      <AIVTable
        :columns="serviceCodeColumns"
        :data="allServiceCodes"
        :item-name="'service codes'"
      >
        <template #cell-archived="{ row }">
          <span :style="{ color: row.archived ? '#f56c6c' : '#67c23a' }">
            {{ row.archived ? "Archived" : "Active" }}
          </span>
        </template>
        <template #cell-create_invoice="{ row }">
          <span :style="{ color: row.create_invoice ? '#67c23a' : '#909399' }">
            {{ row.create_invoice ? "Yes" : "No" }}
          </span>
        </template>
        <template #cell-actions="{ row }">
          <button
            class="btn btn-sm btn-warning mr-2"
            @click="editServiceCode(row)"
            title="Edit"
          >
            <i class="fa-solid fa-pen-to-square"></i>
          </button>
          <button
            :class="['btn', 'btn-sm', row.archived ? 'btn-success' : 'btn-danger']"
            @click="$confirmAction(() => toggleArchiveServiceCode(row))"
            :title="row.archived ? 'Restore' : 'Archive'"
          >
            <i :class="row.archived ? 'fa-solid fa-rotate-left' : 'fa-solid fa-box-archive'"></i>
          </button>
        </template>
      </AIVTable>
    </div>

    <AIVDialog
      v-model="$modal.state.create_or_edit_service_code"
      :title="serviceCodeBeingEdited ? 'Edit Service Code' : 'Create a New Service Code'"
      width="50%"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="serviceCodeForm"
          :model="currentServiceCode"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              { label: 'Code', key: 'code' },
              { label: 'Name', key: 'name' },
            ]"
          />
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Type',
                key: 'type',
                type: 'select',
                options: serviceTypeOptions,
                tooltip:
                  'Determines which USOR billing form this service code is billed on. Pick the type that matches the form to use.',
              },
              { label: 'Rate', key: 'rate', type: 'number' },
            ]"
          />
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Payer Type',
                key: 'payer_type',
                type: 'select',
                options: [
                  { id: 'VR', name: 'VR' },
                  { id: 'DSPD', name: 'DSPD' },
                  { id: 'PP', name: 'PP' },
                ],
              },
              {
                label: 'Unit Type',
                key: 'unit_type',
                type: 'select',
                options: [
                  { id: '', name: 'None' },
                  { id: 'Q', name: 'Q - Quarter Hours' },
                  { id: 'D', name: 'D - Days' },
                  { id: 'S', name: 'S - Sessions' },
                ],
              },
            ]"
          />
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Location Tracking',
                key: 'geofence_mode',
                type: 'select',
                options: [
                  { id: 'capture', name: 'Capture only (record clock-in/out location)' },
                  { id: 'enforce', name: 'Enforce geofence (must be near the client)' },
                  { id: 'off', name: 'Off (no location recorded)' },
                ],
                tooltip:
                  'Clock-in/out location behavior for this code. Capture only records where the caregiver clocked in without blocking. Enforce warns when the caregiver is not within 1320 feet of the client (they can still acknowledge an override). Off records nothing.',
              },
            ]"
          />
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Create Invoice',
                key: 'create_invoice',
                type: 'checkbox',
                tooltip:
                  'When checked, generating billing for shifts creates an invoice for this service code. Uncheck to skip invoice creation during billing generation.',
              },
            ]"
          />
          <AIVRow
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Detection Keywords',
                key: 'aliases',
                type: 'textarea',
                rows: 2,
                placeholder:
                  'e.g. job coaching, JC services (comma separated)',
                tooltip:
                  'Extra phrases the authorization PDF may use for this code. Used to auto-detect the code from uploaded authorization OCR text, in addition to its name and type. Separate multiple phrases with commas.',
              },
            ]"
          />
          <AIVRow
            v-if="currentServiceCode.payer_type === 'VR'"
            :record.sync="currentServiceCode"
            :fields="[
              {
                label: 'Max Activities Selectable',
                key: 'max_activities_select',
                type: 'number',
                placeholder: 'Default 3; blank = no limit',
                tooltip:
                  'Maximum number of \'Activity Completed\' selections a caregiver may pick per shift for this VR code. Leave blank for no limit. Defaults to 3.',
              },
            ]"
          />
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveServiceCode">{{
            serviceCodeBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>

    <div v-if="activeSection === 'non-service-codes'">
      <h5 class="table-page-header">
        Non Service Codes
        <el-tooltip content="Add New Non Service Code">
          <el-button type="text" @click="createCode">
            <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
          </el-button>
        </el-tooltip>
      </h5>
      <AIVTable
        :columns="columns"
        :data="nonServiceCodes"
        :item-name="'non service codes'"
      >
        <template #cell-actions="{ row }">
          <button
            class="btn btn-sm btn-warning mr-2"
            @click="editCode(row)"
            title="Edit"
          >
            <i class="fa-solid fa-pen-to-square"></i>
          </button>
          <button
            class="btn btn-sm btn-danger"
            @click="$confirmAction(() => deleteCode(row))"
            title="Delete"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </template>
      </AIVTable>
    </div>

    <AIVDialog
      v-model="$modal.state.create_or_edit_code"
      :title="codeBeingEdited ? 'Edit Non Service Code' : 'Create a New Non Service Code'"
      width="50%"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="createForm"
          :model="currentCode"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="currentCode"
            :fields="[
              { label: 'Code', key: 'code' },
              { label: 'Name', key: 'name' },
            ]"
          />
          <AIVRow
            :record.sync="currentCode"
            :fields="[
              { label: 'Description', key: 'description', type: 'textarea' },
            ]"
          />
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveCode">{{
            codeBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Shift Note Questions Section -->
    <div v-if="activeSection === 'dspd-shift-questions'">
      <h5 class="table-page-header">
        Shift Note Questions
        <el-tooltip content="Add New Question">
          <el-button
            type="text"
            :disabled="dspdQuestionCap && dspdQuestionCap.reached"
            @click="createQuestion"
          >
            <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
          </el-button>
        </el-tooltip>
      </h5>

      <el-tabs
        :value="selectedServiceCode"
        type="card"
        class="code-tabs"
        @tab-click="(tab) => handleServiceCodeChange(tab.name)"
      >
        <el-tab-pane
          v-for="sc in dspGroupServiceCodes"
          :key="sc.code"
          :label="sc.code"
          :name="sc.code"
        />
      </el-tabs>
      <div class="code-picker-mobile">
        <AIVSelect
          :value="selectedServiceCode"
          :options="dspCodeOptions"
          placeholder="Service Code"
          @input="handleServiceCodeChange"
        />
      </div>
      <p v-if="selectedDspCodeName" class="code-tabs-caption">
        {{ selectedDspCodeName }}
      </p>

      <p v-if="dspdQuestionCap" style="color: #aaa">
        {{ selectedServiceCode }} bills on {{ dspdQuestionCap.formLabel }} —
        max {{ dspdQuestionCap.cap }} questions
        ({{ dspdQuestionCap.used }} of {{ dspdQuestionCap.cap }} used).
      </p>

      <AIVTable
        :columns="questionColumns"
        :data="shiftNoteQuestions"
        :item-name="'shift note questions'"
      >
        <template #cell-question_type="{ row }">
          {{ row.question_type === 'yes_no' ? 'Yes / No' : 'Free Response' }}
        </template>
        <template #cell-required="{ row }">
          {{ row.required ? 'Yes' : 'No' }}
        </template>
        <template #cell-actions="{ row }">
          <button
            class="btn btn-sm btn-warning mr-2"
            @click="editQuestion(row)"
            title="Edit"
          >
            <i class="fa-solid fa-pen-to-square"></i>
          </button>
          <button
            class="btn btn-sm btn-danger"
            @click="$confirmAction(() => deleteQuestion(row))"
            title="Delete"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </template>
      </AIVTable>
    </div>

    <!-- VR Shift Note Questions Section -->
    <div v-if="activeSection === 'vr-shift-questions'">
      <h5 class="table-page-header">
        VR Shift Note Questions
        <el-tooltip content="Add New Question">
          <el-button
            type="text"
            :disabled="vrQuestionCap && vrQuestionCap.reached"
            @click="createVRQuestion"
          >
            <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
          </el-button>
        </el-tooltip>
      </h5>

      <p style="color: #aaa">
        Questions added here appear at clock-out for VR shifts whose service code
        matches the selected code, for every caregiver clocking out on that code.
      </p>

      <div
        v-if="vrGroupServiceCodes.length === 0"
        style="color: #aaa"
        class="mb-2"
      >
        No VR service codes found. Add VR service codes in the Service Codes
        section first.
      </div>
      <template v-else>
        <el-tabs
          :value="selectedVRServiceCode"
          type="card"
          class="code-tabs"
          @tab-click="(tab) => handleVRServiceCodeChange(tab.name)"
        >
          <el-tab-pane
            v-for="sc in vrGroupServiceCodes"
            :key="sc.code"
            :label="sc.code"
            :name="sc.code"
          />
        </el-tabs>
        <div class="code-picker-mobile">
          <AIVSelect
            :value="selectedVRServiceCode"
            :options="vrCodeOptions"
            placeholder="Service Code"
            @input="handleVRServiceCodeChange"
          />
        </div>
        <p v-if="selectedVRCodeName" class="code-tabs-caption">
          {{ selectedVRCodeName }}
        </p>
      </template>

      <p v-if="vrQuestionCap" style="color: #aaa">
        {{ selectedVRServiceCode }} bills on {{ vrQuestionCap.formLabel }} —
        max {{ vrQuestionCap.cap }} questions
        ({{ vrQuestionCap.used }} of {{ vrQuestionCap.cap }} used).
      </p>

      <AIVTable
        v-if="vrGroupServiceCodes.length > 0"
        :columns="questionColumns"
        :data="vrShiftNoteQuestions"
        :item-name="'VR shift note questions'"
      >
        <template #cell-question_type="{ row }">
          {{ row.question_type === 'yes_no' ? 'Yes / No' : 'Free Response' }}
        </template>
        <template #cell-required="{ row }">
          {{ row.required ? 'Yes' : 'No' }}
        </template>
        <template #cell-actions="{ row }">
          <button
            class="btn btn-sm btn-warning mr-2"
            @click="editVRQuestion(row)"
            title="Edit"
          >
            <i class="fa-solid fa-pen-to-square"></i>
          </button>
          <button
            class="btn btn-sm btn-danger"
            @click="$confirmAction(() => deleteVRQuestion(row))"
            title="Delete"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </template>
      </AIVTable>
    </div>

    <!-- VR Activity Lists Section -->
    <div v-if="activeSection === 'vr-activity-lists'">
      <h5 class="table-page-header">
        VR Activity Lists
        <el-tooltip content="Add New Activity">
          <el-button type="text" @click="createActivity">
            <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
          </el-button>
        </el-tooltip>
      </h5>

      <p style="color: #aaa">
        These activities appear in the "Activity Completed" picker when
        documenting shifts for the selected VR service code. The per-code
        selection limit is set by "Max Activities Selectable" on the service
        code itself (Service Codes section).
      </p>

      <div
        v-if="activityServiceCodes.length === 0"
        style="color: #aaa"
        class="mb-2"
      >
        No eligible VR service codes found. Add VR service codes in the
        Service Codes section first.
      </div>
      <template v-else>
        <el-tabs
          :value="selectedActivityServiceCode"
          type="card"
          class="code-tabs"
          @tab-click="(tab) => handleActivityServiceCodeChange(tab.name)"
        >
          <el-tab-pane
            v-for="sc in activityServiceCodes"
            :key="sc.code"
            :label="sc.code"
            :name="sc.code"
          />
        </el-tabs>
        <div class="code-picker-mobile">
          <AIVSelect
            :value="selectedActivityServiceCode"
            :options="activityCodeOptions"
            placeholder="Service Code"
            @input="handleActivityServiceCodeChange"
          />
        </div>
        <p v-if="selectedActivityCodeLabel" class="code-tabs-caption">
          {{ selectedActivityCodeLabel }}
        </p>
      </template>

      <div v-if="vrActivities.length === 0" style="color: #aaa">
        No activities yet. Use the + above to add one.
      </div>
      <p v-else style="color: #aaa">
        Drag the <i class="fa-solid fa-grip-vertical"></i> handle to reorder.
        Order is saved automatically.
      </p>

      <draggable
        v-model="vrActivities"
        handle=".activity-drag-handle"
        ghost-class="activity-row-ghost"
        :animation="150"
        @end="onActivityReorder"
        class="vr-activity-list"
      >
        <div
          v-for="activity in vrActivities"
          :key="activity.id"
          class="vr-activity-row"
        >
          <i
            class="fa-solid fa-grip-vertical activity-drag-handle"
            title="Drag to reorder"
          ></i>
          <span class="vr-activity-name">{{ activity.name }}</span>
          <span
            class="vr-activity-status"
            :style="{ color: activity.archived ? '#e6a23c' : '#67c23a' }"
          >
            {{ activity.archived ? 'Archived' : 'Active' }}
          </span>
          <button
            class="btn btn-sm btn-warning mr-2"
            @click="editActivity(activity)"
            title="Edit"
          >
            <i class="fa-solid fa-pen-to-square"></i>
          </button>
          <button
            class="btn btn-sm btn-danger"
            @click="$confirmAction(() => deleteActivity(activity))"
            title="Delete"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </draggable>
    </div>

    <AIVDialog
      v-model="$modal.state.create_or_edit_activity"
      :title="activityBeingEdited ? 'Edit Activity' : 'Add Activity'"
      width="50%"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="activityForm"
          :model="currentActivity"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="currentActivity"
            :fields="[
              { label: 'Activity Name', key: 'name', type: 'textarea' },
            ]"
          />
          <div class="mt-3 ml-3">
            <span style="color: white; margin-right: 10px">Active:</span>
            <el-switch v-model="currentActivity.is_active"></el-switch>
          </div>
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveActivity">{{
            activityBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- DSPD Low Units Email Notifications -->
    <div v-if="activeSection === 'dspd-email-alerts'">
      <h5 class="table-page-header">DSPD Low Units Email Recipients</h5>
      <p style="color: #aaa">Select which admins receive a notification email when a DSPD authorization falls below its low units threshold.</p>

      <div class="settings-content">
        <div
          v-for="setting in dspdEmailSettings"
          :key="setting.service_code_id"
          class="setting-row"
        >
          <label class="setting-label">
            {{ setting.name }}
          </label>
          <AIVSelect
            v-model="setting.admin_user_ids"
            :options="adminUserOptions"
            :multiSelect="true"
            :showSelectAll="true"
            placeholder="Select admins..."
            class="setting-select"
          />
        </div>
      </div>

      <div class="mt-3">
        <el-button type="primary" round @click="saveDSPDEmailSettings">Save Settings</el-button>
      </div>
    </div>

    <!-- AI Email Prompts (per VR service code) -->
    <div v-if="activeSection === 'ai-email-prompts'">
      <h5 class="table-page-header">AI Email Prompts</h5>
      <p style="color: #aaa">
        Prompt used to generate the AI authorization-request email for each VR
        service code (the Email Authorization Tracking dashboard). Codes
        without a saved prompt use the generic default shown as the
        placeholder. Available tokens:
        <code v-pre>{{clientName}}</code>, <code v-pre>{{counselorName}}</code> and
        <code v-pre>{{lifeSkillsCourses}}</code> (inserts the Life Skills course
        catalog — removing it from LS drops the catalog from its emails).
        Client background notes, formatting rules, and shift notes are appended
        automatically — don't add them here.
      </p>

      <div v-if="aiPromptCodes.length === 0" style="color: #aaa" class="mb-2">
        No VR service codes found. Add VR service codes in the Service Codes
        section first.
      </div>
      <template v-else>
        <el-tabs
          :value="selectedAiPromptCode"
          type="card"
          class="code-tabs"
          @tab-click="(tab) => handleAiPromptCodeChange(tab.name)"
        >
          <el-tab-pane
            v-for="sc in aiPromptCodes"
            :key="sc.code"
            :label="sc.code"
            :name="sc.code"
          />
        </el-tabs>
        <div class="code-picker-mobile">
          <AIVSelect
            :value="selectedAiPromptCode"
            :options="aiPromptCodeOptions"
            placeholder="Service Code"
            @input="handleAiPromptCodeChange"
          />
        </div>
        <p v-if="selectedAiPromptLabel" class="code-tabs-caption">
          {{ selectedAiPromptLabel }}
          <span
            v-if="selectedAiPromptRow && !selectedAiPromptRow.ai_email_prompt"
            style="color: #e6a23c"
          >
            — using generic default
          </span>
        </p>

        <textarea
          v-model="aiPromptDraft"
          rows="14"
          class="form-control"
          :placeholder="genericDefaultPrompt"
        ></textarea>

        <div class="mt-3">
          <el-button type="primary" round @click="saveAiPrompt">Save</el-button>
          <el-button round @click="$confirmAction(resetAiPromptToDefault)">
            Use Generic Default
          </el-button>
        </div>
      </template>
    </div>

    <!-- Mileage Settings -->
    <div v-if="activeSection === 'mileage-settings'">
      <h5 class="table-page-header">Mileage Settings</h5>
      <p style="color: #aaa">
        Caregivers can record odometer readings on any shift. Shifts marked
        "Transporting client(s)" are reimbursed in full; driving without
        clients has the daily deduction below subtracted once per day.
        Managers are notified when a caregiver's estimated reimbursement for
        a pay period reaches the alert threshold of the cap.
      </p>

      <div class="settings-content" style="max-width: 560px">
        <div class="setting-row">
          <label class="setting-label">Daily deduction (miles, without clients)</label>
          <el-input-number
            v-model="mileageSettings.daily_deduction_miles"
            :min="0"
            :step="1"
            :precision="1"
            controls-position="right"
          />
        </div>
        <div class="setting-row">
          <label class="setting-label">Reimbursement rate ($ per mile)</label>
          <el-input-number
            v-model="mileageSettings.reimbursement_rate"
            :min="0"
            :step="0.01"
            :precision="3"
            controls-position="right"
          />
        </div>
        <div class="setting-row">
          <label class="setting-label">Pay-period cap ($)</label>
          <el-input-number
            v-model="mileageSettings.pay_period_cap"
            :min="0"
            :step="5"
            :precision="2"
            controls-position="right"
          />
        </div>
        <div class="setting-row">
          <label class="setting-label">Manager alert at (% of cap)</label>
          <el-input-number
            v-model="mileageSettings.cap_alert_threshold_percent"
            :min="1"
            :max="100"
            :step="5"
            controls-position="right"
          />
        </div>
      </div>

      <div class="mt-3">
        <el-button type="primary" round @click="saveMileageSettings">Save Settings</el-button>
      </div>
    </div>

    <AIVDialog
      v-model="$modal.state.create_or_edit_question"
      :title="questionBeingEdited ? 'Edit Shift Note Question' : 'Add Shift Note Question'"
      width="50%"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="questionForm"
          :model="currentQuestion"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="currentQuestion"
            :fields="[
              { label: 'Question Text', key: 'question_text', type: 'textarea' },
            ]"
          />
          <AIVRow
            :record.sync="currentQuestion"
            :fields="[
              {
                label: 'Guidance (optional)',
                key: 'description',
                type: 'textarea',
                rows: 4,
                placeholder: 'Shown under the question at clock-out, e.g. bullet points of what to include',
              },
            ]"
          />
          <AIVRow
            :record.sync="currentQuestion"
            :fields="[
              {
                label: 'Type',
                key: 'question_type',
                type: 'select',
                options: questionTypeOptions,
              },
              { label: 'Sort Order', key: 'sort_order' },
            ]"
          />
          <div v-if="currentQuestion.question_type === 'select' || currentQuestion.question_type === 'multi_select'" class="mt-3 ml-3">
            <ShiftQuestionOptionsEditor
              :value="currentQuestion.options || []"
              @input="$set(currentQuestion, 'options', $event)"
            />
          </div>
          <div class="mt-3 ml-3">
            <span style="color: white; margin-right: 10px">Required:</span>
            <el-switch v-model="currentQuestion.required"></el-switch>
          </div>
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveQuestion">{{
            questionBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>

    <AIVDialog
      v-model="$modal.state.create_or_edit_vr_question"
      :title="vrQuestionBeingEdited ? 'Edit VR Shift Note Question' : 'Add VR Shift Note Question'"
      width="50%"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="vrQuestionForm"
          :model="currentVRQuestion"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="currentVRQuestion"
            :fields="[
              { label: 'Question Text', key: 'question_text', type: 'textarea' },
            ]"
          />
          <AIVRow
            :record.sync="currentVRQuestion"
            :fields="[
              {
                label: 'Guidance (optional)',
                key: 'description',
                type: 'textarea',
                rows: 4,
                placeholder: 'Shown under the question at clock-out, e.g. bullet points of what to include',
              },
            ]"
          />
          <AIVRow
            :record.sync="currentVRQuestion"
            :fields="[
              {
                label: 'Type',
                key: 'question_type',
                type: 'select',
                options: questionTypeOptions,
              },
              { label: 'Sort Order', key: 'sort_order' },
            ]"
          />
          <div v-if="currentVRQuestion.question_type === 'select' || currentVRQuestion.question_type === 'multi_select'" class="mt-3 ml-3">
            <ShiftQuestionOptionsEditor
              :value="currentVRQuestion.options || []"
              @input="$set(currentVRQuestion, 'options', $event)"
            />
          </div>
          <div class="mt-3 ml-3">
            <span style="color: white; margin-right: 10px">Required:</span>
            <el-switch v-model="currentVRQuestion.required"></el-switch>
          </div>
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveVRQuestion">{{
            vrQuestionBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { nonServiceCodesService } from "@/serviceHandlers/nonServiceCodesService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { vrActivitiesService } from "@/serviceHandlers/vrActivitiesService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { dspdEmailSettingsService } from "@/serviceHandlers/dspdEmailSettingsService";
import { mileageSettingsService } from "@/serviceHandlers/mileageSettingsService";
import { authorizationEmailsService } from "@/serviceHandlers/authorizationEmailsService";
import { adminUsersService } from "@/serviceHandlers/adminUsersService";

import { useLoginStore } from "@/stores/loginStore";
import ShiftQuestionOptionsEditor from "@/components/ShiftQuestionOptionsEditor.vue";
import draggable from "vuedraggable";
import {
  getServiceTypeOptions,
  getBillingFormForType,
  getMaxQuestionsForType,
} from "@/constants/billingForms";

export default {
  name: "Settings",
  components: {
    ShiftQuestionOptionsEditor,
    draggable,
  },
  data() {
    return {
      // Section registry: drives the landing menu and the sidebar submenu
      // (paths in DashboardLayout.vue must match these ids)
      sections: [
        {
          id: "service-codes",
          title: "Service Codes",
          icon: "fa-solid fa-tags",
          desc: "Billable codes, rates, and billing forms",
        },
        {
          id: "non-service-codes",
          title: "Non-Service Codes",
          icon: "fa-solid fa-list",
          desc: "Codes for non-billable shift time",
        },
        {
          id: "dspd-shift-questions",
          title: "DSPD Shift Questions",
          icon: "fa-solid fa-clipboard-question",
          desc: "Clock-out questions for DSPD service codes",
        },
        {
          id: "vr-shift-questions",
          title: "VR Shift Questions",
          icon: "fa-solid fa-clipboard-question",
          desc: "Clock-out questions for VR service codes",
        },
        {
          id: "vr-activity-lists",
          title: "VR Activity Lists",
          icon: "fa-solid fa-list-check",
          desc: "\"Activity Completed\" picker options per VR code",
        },
        {
          id: "dspd-email-alerts",
          title: "DSPD Low Units Emails",
          icon: "fa-solid fa-bell",
          desc: "Email recipients for low-unit alerts",
        },
        {
          id: "ai-email-prompts",
          title: "AI Email Prompts",
          icon: "fa-solid fa-wand-magic-sparkles",
          desc: "AI authorization-email prompt per VR code",
        },
        {
          id: "mileage-settings",
          title: "Mileage Settings",
          icon: "fa-solid fa-car",
          desc: "Daily deduction, reimbursement rate, and pay-period cap",
        },
      ],

      // Mileage settings (singleton)
      mileageSettings: {
        daily_deduction_miles: null,
        reimbursement_rate: null,
        pay_period_cap: null,
        cap_alert_threshold_percent: null,
      },

      // Service Codes
      serviceCodeBeingEdited: null,
      serviceCodeColumns: [
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "type", label: "Type" },
        { key: "rate", label: "Rate" },
        { key: "payer_type", label: "Payer" },
        { key: "unit_type", label: "Unit" },
        { key: "create_invoice", label: "Invoice", sortable: false },
        { key: "archived", label: "Status", sortable: false },
        { key: "actions", label: "Actions", sortable: false },
      ],
      allServiceCodes: [],
      newServiceCode: {
        code: "",
        name: "",
        type: "",
        rate: null,
        payer_type: "VR",
        unit_type: "",
      },

      codeBeingEdited: null,
      columns: [
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "description", label: "Description" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newCode: {
        code: "",
        name: "",
        description: "",
      },
      nonServiceCodes: [],

      // Shift Note Questions
      selectedServiceCode: "DSG",
      shiftNoteQuestions: [],
      questionBeingEdited: null,
      newQuestion: {
        question_text: "",
        question_type: "free_response",
        sort_order: 1,
        required: true,
        options: [],
      },
      questionColumns: [
        { key: "sort_order", label: "Order" },
        { key: "question_text", label: "Question" },
        { key: "question_type", label: "Type", sortable: false },
        { key: "required", label: "Required", sortable: false },
        { key: "actions", label: "Actions", sortable: false },
      ],
      questionTypeOptions: [
        { id: "free_response", name: "Free Response" },
        { id: "yes_no", name: "Yes / No" },
        { id: "select", name: "Select (dropdown)" },
        { id: "multi_select", name: "Multi-Select (choose multiple)" },
      ],
      serviceCodeIdMap: {},
      dspGroupServiceCodes: [],

      // VR Shift Note Questions
      selectedVRServiceCode: "",
      vrShiftNoteQuestions: [],
      vrQuestionBeingEdited: null,
      newVRQuestion: {
        question_text: "",
        question_type: "free_response",
        sort_order: 1,
        required: true,
        options: [],
      },
      vrServiceCodeIdMap: {},
      vrGroupServiceCodes: [],

      // VR Activity Lists — dynamic over all VR service codes (excluding JC and
      // the JD codes, which have bespoke pickers).
      selectedActivityServiceCode: null,
      vrActivities: [],
      activityBeingEdited: null,
      newActivity: {
        name: "",
        sort_order: 1,
        is_active: true,
      },
      // Codes excluded from the generic VR activity system.
      activityExcludedCodes: ["JC", "CE", "SE", "SJBT", "TWE"],
      activityServiceCodes: [],

      // DSPD Email Notification Settings
      dspdEmailSettings: [],
      adminUserOptions: [],

      // AI Email Prompts
      aiPromptCodes: [],
      genericDefaultPrompt: "",
      selectedAiPromptCode: null,
      aiPromptDraft: "",
    };
  },
  computed: {
    isAdmin() {
      const loginStore = useLoginStore();
      return loginStore?.user?.user_type === 'Admin';
    },
    // Active settings section from the route (/pages/settings/:section?).
    // Unknown values fall back to the landing menu.
    activeSection() {
      const s = this.$route.params.section;
      return this.sections.some((x) => x.id === s) ? s : null;
    },
    // Full names of the code selected in each tab strip, shown as a caption
    selectedDspCodeName() {
      const sc = this.dspGroupServiceCodes.find(
        (x) => x.code === this.selectedServiceCode,
      );
      return sc ? sc.name : "";
    },
    selectedVRCodeName() {
      const sc = this.vrGroupServiceCodes.find(
        (x) => x.code === this.selectedVRServiceCode,
      );
      return sc ? sc.name : "";
    },
    selectedActivityCodeLabel() {
      const sc = this.activityServiceCodes.find(
        (x) => x.code === this.selectedActivityServiceCode,
      );
      return sc ? `${sc.code} — ${sc.label}` : "";
    },
    selectedAiPromptRow() {
      return (
        this.aiPromptCodes.find(
          (sc) => sc.code === this.selectedAiPromptCode,
        ) || null
      );
    },
    selectedAiPromptLabel() {
      const sc = this.selectedAiPromptRow;
      return sc ? `${sc.code} — ${sc.name}` : "";
    },
    aiPromptCodeOptions() {
      return this.aiPromptCodes.map((sc) => ({
        id: sc.code,
        name: `${sc.code} — ${sc.name}`,
      }));
    },
    // Same code lists shaped for the mobile dropdown pickers
    dspCodeOptions() {
      return this.dspGroupServiceCodes.map((sc) => ({
        id: sc.code,
        name: sc.name,
      }));
    },
    vrCodeOptions() {
      return this.vrGroupServiceCodes.map((sc) => ({
        id: sc.code,
        name: sc.name,
      }));
    },
    activityCodeOptions() {
      return this.activityServiceCodes.map((sc) => ({
        id: sc.code,
        name: `${sc.code} — ${sc.label}`,
      }));
    },
    // Options for the service code "Type" dropdown. Type links a service code
    // to the USOR billing form it is billed on (see constants/billingForms.js).
    serviceTypeOptions() {
      return getServiceTypeOptions();
    },
    currentServiceCode: {
      get() {
        return this.serviceCodeBeingEdited || this.newServiceCode;
      },
      set(val) {
        if (this.serviceCodeBeingEdited) {
          this.serviceCodeBeingEdited = val;
        } else {
          this.newServiceCode = val;
        }
      },
    },
    currentCode: {
      get() {
        return this.codeBeingEdited || this.newCode;
      },
      set(val) {
        if (this.codeBeingEdited) {
          this.codeBeingEdited = val;
        } else {
          this.newCode = val;
        }
      },
    },
    // Cap on configurable questions for the selected code in each section,
    // driven by the USOR form its type bills on. Null = no cap (DSPD, blank).
    dspdQuestionCap() {
      return this.questionCapFor(
        this.dspGroupServiceCodes,
        this.selectedServiceCode,
        this.shiftNoteQuestions,
      );
    },
    vrQuestionCap() {
      return this.questionCapFor(
        this.vrGroupServiceCodes,
        this.selectedVRServiceCode,
        this.vrShiftNoteQuestions,
      );
    },
    currentQuestion: {
      get() {
        return this.questionBeingEdited || this.newQuestion;
      },
      set(val) {
        if (this.questionBeingEdited) {
          this.questionBeingEdited = val;
        } else {
          this.newQuestion = val;
        }
      },
    },
    currentVRQuestion: {
      get() {
        return this.vrQuestionBeingEdited || this.newVRQuestion;
      },
      set(val) {
        if (this.vrQuestionBeingEdited) {
          this.vrQuestionBeingEdited = val;
        } else {
          this.newVRQuestion = val;
        }
      },
    },
    currentActivity: {
      get() {
        return this.activityBeingEdited || this.newActivity;
      },
      set(val) {
        if (this.activityBeingEdited) {
          this.activityBeingEdited = val;
        } else {
          this.newActivity = val;
        }
      },
    },
  },
  watch: {
    activeSection: {
      immediate: true,
      handler(section) {
        if (section) this.loadSection(section);
      },
    },
  },
  methods: {
    // --- Section loading (driven by the route param) ---
    async loadSection(section) {
      switch (section) {
        case "service-codes":
          if (this.allServiceCodes.length === 0) this.fetchServiceCodes();
          break;
        case "non-service-codes":
          if (this.nonServiceCodes.length === 0) this.fetchCodes();
          break;
        case "dspd-shift-questions":
          if (Object.keys(this.serviceCodeIdMap).length === 0) {
            await this.fetchServiceCodeIds();
          }
          if (this.shiftNoteQuestions.length === 0) this.fetchQuestions();
          break;
        case "vr-shift-questions":
          if (this.vrGroupServiceCodes.length === 0) {
            await this.fetchVRServiceCodeIds();
          }
          if (this.selectedVRServiceCode) this.fetchVRQuestions();
          break;
        case "vr-activity-lists":
          await this.loadActivityServiceCodes();
          if (this.selectedActivityServiceCode) await this.fetchVRActivities();
          break;
        case "dspd-email-alerts":
          if (this.dspdEmailSettings.length === 0) {
            await this.fetchDSPDEmailSettings();
          }
          break;
        case "ai-email-prompts":
          await this.loadAiPromptCodes();
          break;
        case "mileage-settings":
          await this.fetchMileageSettings();
          break;
      }
    },

    // --- AI Email Prompts ---
    async loadAiPromptCodes() {
      this.$spinner.show();
      try {
        const res = await authorizationEmailsService.getSupportedCodes();
        this.aiPromptCodes = res.supportedCodes || [];
        this.genericDefaultPrompt = res.genericDefaultPrompt || "";
        if (
          !this.selectedAiPromptCode ||
          !this.aiPromptCodes.some((sc) => sc.code === this.selectedAiPromptCode)
        ) {
          this.selectedAiPromptCode =
            this.aiPromptCodes.length > 0 ? this.aiPromptCodes[0].code : null;
        }
        this.syncAiPromptDraft();
      } catch (error) {
        this.$notifyError("Could not load AI email prompts: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    handleAiPromptCodeChange(code) {
      this.selectedAiPromptCode = code;
      this.syncAiPromptDraft();
    },
    syncAiPromptDraft() {
      this.aiPromptDraft = this.selectedAiPromptRow?.ai_email_prompt || "";
    },
    async saveAiPrompt() {
      const row = this.selectedAiPromptRow;
      if (!row) return;
      const value = this.aiPromptDraft.trim() || null;
      this.$spinner.show();
      try {
        await serviceCodesService.update(row.id, { ai_email_prompt: value });
        row.ai_email_prompt = value;
        this.aiPromptDraft = value || "";
        this.$notifySuccess(
          value
            ? `Saved AI email prompt for ${row.code}.`
            : `${row.code} will now use the generic default prompt.`,
        );
      } catch (error) {
        this.$notifyError("Could not save AI email prompt: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    async resetAiPromptToDefault() {
      this.aiPromptDraft = "";
      await this.saveAiPrompt();
    },

    // --- Service Codes ---
    async fetchServiceCodes() {
      this.$spinner.show();
      try {
        this.allServiceCodes = await serviceCodesService.listAll();
      } catch (error) {
        this.$notifyError("Could not load service codes: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    createServiceCode() {
      this.serviceCodeBeingEdited = null;
      this.newServiceCode = {
        code: "",
        name: "",
        type: "",
        rate: null,
        payer_type: "VR",
        unit_type: "",
        aliases: "",
        create_invoice: true,
        geofence_mode: "capture",
      };
      this.$modal.open("create_or_edit_service_code");
    },
    editServiceCode(row) {
      this.serviceCodeBeingEdited = { ...row };
      this.$modal.open("create_or_edit_service_code");
    },
    async saveServiceCode() {
      const codeObj = this.currentServiceCode;

      if (!codeObj.code) {
        this.$notifyError("Code is required!");
        return;
      }
      if (!codeObj.name) {
        this.$notifyError("Name is required!");
        return;
      }
      if (!codeObj.payer_type) {
        this.$notifyError("Payer type is required!");
        return;
      }

      // Normalize the invoice flag to a boolean; missing => true (default).
      codeObj.create_invoice =
        codeObj.create_invoice === undefined || codeObj.create_invoice === null
          ? true
          : !!codeObj.create_invoice;

      // Missing location mode => capture only (the default behavior).
      if (!codeObj.geofence_mode) {
        codeObj.geofence_mode = "capture";
      }

      // Normalize the per-code activity cap: blank => null (no limit), else int.
      if (
        codeObj.max_activities_select === "" ||
        codeObj.max_activities_select === undefined
      ) {
        codeObj.max_activities_select = null;
      } else if (codeObj.max_activities_select !== null) {
        const n = parseInt(codeObj.max_activities_select, 10);
        codeObj.max_activities_select = isNaN(n) ? null : n;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.serviceCodeBeingEdited) {
        apiSaveCall = serviceCodesService
          .update(codeObj.id, codeObj)
          .then(() => {
            this.$notifySuccess("Successfully updated service code.");
          });
      } else {
        apiSaveCall = serviceCodesService.create(codeObj).then(() => {
          this.$notifySuccess("Successfully created service code.");
        });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .then(() => {
          return this.fetchServiceCodes();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_service_code");
          this.serviceCodeBeingEdited = null;
        });
    },
    async toggleArchiveServiceCode(row) {
      try {
        await serviceCodesService.archive(row.id);
        this.$notifySuccess(
          row.archived
            ? "Service code restored successfully!"
            : "Service code archived successfully!"
        );
        this.fetchServiceCodes();
      } catch (error) {
        this.$notifyError("Error archiving service code: " + error);
      }
    },

    // --- Non Service Codes ---
    async fetchCodes() {
      this.$spinner.show();
      try {
        this.nonServiceCodes = await nonServiceCodesService.list();
      } catch (error) {
        this.$notifyError("Could not load non service codes: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    createCode() {
      this.codeBeingEdited = null;
      this.newCode = {
        code: "",
        name: "",
        description: "",
      };
      this.$modal.open("create_or_edit_code");
    },
    editCode(row) {
      this.codeBeingEdited = { ...row };
      this.$modal.open("create_or_edit_code");
    },
    async saveCode() {
      const codeObj = this.currentCode;

      if (!codeObj.code) {
        this.$notifyError("Code is required!");
        return;
      }
      if (!codeObj.name) {
        this.$notifyError("Name is required!");
        return;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.codeBeingEdited) {
        apiSaveCall = nonServiceCodesService
          .update(codeObj.id, codeObj)
          .then(() => {
            this.$notifySuccess("Successfully updated non service code.");
          });
      } else {
        apiSaveCall = nonServiceCodesService.create(codeObj).then(() => {
          this.$notifySuccess("Successfully created non service code.");
        });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .then(() => {
          return this.fetchCodes();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_code");
          this.codeBeingEdited = null;
        });
    },
    deleteCode(row) {
      return nonServiceCodesService
        .delete(row.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted non service code!");
          this.fetchCodes();
        })
        .catch((error) => {
          this.$notifyError("Error deleting non service code: " + error);
        });
    },

    // --- Shift Note Questions ---
    questionCapFor(codeList, selectedCode, questions) {
      const sc = codeList.find((c) => c.code === selectedCode);
      const cap = sc ? getMaxQuestionsForType(sc.type) : null;
      if (cap == null) return null;
      return {
        cap,
        used: questions.length,
        reached: questions.length >= cap,
        formLabel: getBillingFormForType(sc.type).label,
      };
    },
    questionCapMessage(code, capState) {
      return (
        `${code} bills on ${capState.formLabel}, which allows at most ` +
        `${capState.cap} clock-out question${capState.cap === 1 ? "" : "s"}.`
      );
    },
    async fetchServiceCodeIds() {
      try {
        const codes = await serviceCodesService.listDSPD();
        const list = Array.isArray(codes) ? codes : codes.data || [];
        this.dspGroupServiceCodes = list.map((sc) => ({
          code: sc.code,
          name: `${sc.code} - ${sc.name}`,
          type: sc.type,
        }));
        list.forEach((sc) => {
          this.serviceCodeIdMap[sc.code] = sc.id;
        });
        // Default to first code if current selection isn't in the list
        if (
          this.dspGroupServiceCodes.length > 0 &&
          !this.dspGroupServiceCodes.some(
            (sc) => sc.code === this.selectedServiceCode,
          )
        ) {
          this.selectedServiceCode = this.dspGroupServiceCodes[0].code;
        }
      } catch (error) {
        this.$notifyError("Could not load service codes: " + error);
      }
    },
    async fetchQuestions() {
      this.$spinner.show();
      try {
        this.shiftNoteQuestions = await shiftNoteQuestionsService.getByServiceCode(this.selectedServiceCode);
      } catch (error) {
        this.$notifyError("Could not load shift note questions: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    handleServiceCodeChange(code) {
      if (!code || code === this.selectedServiceCode) return;
      this.selectedServiceCode = code;
      this.fetchQuestions();
    },
    createQuestion() {
      this.questionBeingEdited = null;
      const nextOrder = this.shiftNoteQuestions.length > 0
        ? Math.max(...this.shiftNoteQuestions.map((q) => q.sort_order || 0)) + 1
        : 1;
      this.newQuestion = {
        question_text: "",
        question_type: "free_response",
        sort_order: nextOrder,
        required: true,
        options: [],
        description: "",
      };
      this.$modal.open("create_or_edit_question");
    },
    editQuestion(row) {
      this.questionBeingEdited = {
        ...row,
        options: Array.isArray(row.options) ? [...row.options] : [],
      };
      this.$modal.open("create_or_edit_question");
    },
    // Normalize a question before saving: only keep options for "select" and
    // "multi_select" questions, trimming blanks; clear options otherwise.
    cleanQuestionPayload(questionObj) {
      const payload = { ...questionObj };
      if (payload.question_type === "select" || payload.question_type === "multi_select") {
        payload.options = (payload.options || [])
          .map((o) => (o == null ? "" : o.toString().trim()))
          .filter((o) => o.length > 0);
      } else {
        payload.options = null;
      }
      return payload;
    },
    async saveQuestion() {
      const questionObj = this.currentQuestion;

      if (!questionObj.question_text) {
        this.$notifyError("Question text is required!");
        return;
      }
      if (
        !this.questionBeingEdited &&
        this.dspdQuestionCap &&
        this.dspdQuestionCap.reached
      ) {
        this.$notifyError(
          this.questionCapMessage(this.selectedServiceCode, this.dspdQuestionCap),
        );
        return;
      }
      const payload = this.cleanQuestionPayload(questionObj);
      if (
        (payload.question_type === "select" ||
          payload.question_type === "multi_select") &&
        payload.options.length < 1
      ) {
        this.$notifyError("Add at least one response option for a Select question.");
        return;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.questionBeingEdited) {
        apiSaveCall = shiftNoteQuestionsService
          .update(payload.id, payload)
          .then(() => {
            this.$notifySuccess("Successfully updated question.");
          });
      } else {
        apiSaveCall = shiftNoteQuestionsService
          .create({
            ...payload,
            service_code_id: this.serviceCodeIdMap[this.selectedServiceCode],
          })
          .then(() => {
            this.$notifySuccess("Successfully created question.");
          });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError(
            error.response?.data?.message || "Error: " + error,
          );
        })
        .then(() => {
          return this.fetchQuestions();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_question");
          this.questionBeingEdited = null;
        });
    },
    deleteQuestion(row) {
      return shiftNoteQuestionsService
        .delete(row.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted question!");
          this.fetchQuestions();
        })
        .catch((error) => {
          this.$notifyError("Error deleting question: " + error);
        });
    },

    // --- VR Shift Note Questions ---
    async fetchVRServiceCodeIds() {
      try {
        const codes = await serviceCodesService.listVR();
        const list = Array.isArray(codes) ? codes : codes.data || [];
        this.vrGroupServiceCodes = list.map((sc) => ({
          code: sc.code,
          name: `${sc.code} - ${sc.name}`,
          type: sc.type,
        }));
        const idMap = {};
        list.forEach((sc) => {
          idMap[sc.code] = sc.id;
        });
        this.vrServiceCodeIdMap = idMap;
        // Default to first code if current selection isn't in the list
        if (
          this.vrGroupServiceCodes.length > 0 &&
          !this.vrGroupServiceCodes.some(
            (sc) => sc.code === this.selectedVRServiceCode,
          )
        ) {
          this.selectedVRServiceCode = this.vrGroupServiceCodes[0].code;
        }
      } catch (error) {
        this.$notifyError("Could not load VR service codes: " + error);
      }
    },
    async fetchVRQuestions() {
      if (!this.selectedVRServiceCode) {
        this.vrShiftNoteQuestions = [];
        return;
      }
      this.$spinner.show();
      try {
        this.vrShiftNoteQuestions =
          await shiftNoteQuestionsService.getByServiceCode(
            this.selectedVRServiceCode,
          );
      } catch (error) {
        this.$notifyError("Could not load VR shift note questions: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    handleVRServiceCodeChange(code) {
      if (!code || code === this.selectedVRServiceCode) return;
      this.selectedVRServiceCode = code;
      this.fetchVRQuestions();
    },
    createVRQuestion() {
      this.vrQuestionBeingEdited = null;
      const nextOrder =
        this.vrShiftNoteQuestions.length > 0
          ? Math.max(
              ...this.vrShiftNoteQuestions.map((q) => q.sort_order || 0),
            ) + 1
          : 1;
      this.newVRQuestion = {
        question_text: "",
        question_type: "free_response",
        sort_order: nextOrder,
        required: true,
        options: [],
        description: "",
      };
      this.$modal.open("create_or_edit_vr_question");
    },
    editVRQuestion(row) {
      this.vrQuestionBeingEdited = {
        ...row,
        options: Array.isArray(row.options) ? [...row.options] : [],
      };
      this.$modal.open("create_or_edit_vr_question");
    },
    async saveVRQuestion() {
      const questionObj = this.currentVRQuestion;

      if (!questionObj.question_text) {
        this.$notifyError("Question text is required!");
        return;
      }
      if (!this.selectedVRServiceCode) {
        this.$notifyError("Select a VR service code first!");
        return;
      }
      if (
        !this.vrQuestionBeingEdited &&
        this.vrQuestionCap &&
        this.vrQuestionCap.reached
      ) {
        this.$notifyError(
          this.questionCapMessage(this.selectedVRServiceCode, this.vrQuestionCap),
        );
        return;
      }
      const payload = this.cleanQuestionPayload(questionObj);
      if (
        (payload.question_type === "select" ||
          payload.question_type === "multi_select") &&
        payload.options.length < 1
      ) {
        this.$notifyError("Add at least one response option for a Select question.");
        return;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.vrQuestionBeingEdited) {
        apiSaveCall = shiftNoteQuestionsService
          .update(payload.id, payload)
          .then(() => {
            this.$notifySuccess("Successfully updated question.");
          });
      } else {
        apiSaveCall = shiftNoteQuestionsService
          .create({
            ...payload,
            service_code_id:
              this.vrServiceCodeIdMap[this.selectedVRServiceCode],
          })
          .then(() => {
            this.$notifySuccess("Successfully created question.");
          });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError(
            error.response?.data?.message || "Error: " + error,
          );
        })
        .then(() => {
          return this.fetchVRQuestions();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_vr_question");
          this.vrQuestionBeingEdited = null;
        });
    },
    deleteVRQuestion(row) {
      return shiftNoteQuestionsService
        .delete(row.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted question!");
          this.fetchVRQuestions();
        })
        .catch((error) => {
          this.$notifyError("Error deleting question: " + error);
        });
    },

    // --- VR Activity Lists (any VR code) ---
    async loadActivityServiceCodes() {
      try {
        const vrCodes = await serviceCodesService.listVR();
        this.activityServiceCodes = (vrCodes || [])
          .filter((sc) => !this.activityExcludedCodes.includes(sc.code))
          .map((sc) => ({ code: sc.code, label: sc.name }));
        // Default to the first available code if none selected / now invalid.
        if (
          !this.selectedActivityServiceCode ||
          !this.activityServiceCodes.some(
            (sc) => sc.code === this.selectedActivityServiceCode,
          )
        ) {
          this.selectedActivityServiceCode =
            this.activityServiceCodes.length > 0
              ? this.activityServiceCodes[0].code
              : null;
        }
      } catch (error) {
        this.$notifyError("Could not load VR service codes: " + error);
      }
    },
    async fetchVRActivities() {
      if (!this.selectedActivityServiceCode) {
        this.vrActivities = [];
        return;
      }
      this.$spinner.show();
      try {
        // Include archived rows so admins can manage and restore them.
        this.vrActivities = await vrActivitiesService.listAllByServiceCode(
          this.selectedActivityServiceCode,
        );
      } catch (error) {
        this.$notifyError("Could not load VR activities: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    handleActivityServiceCodeChange(code) {
      if (!code || code === this.selectedActivityServiceCode) return;
      this.selectedActivityServiceCode = code;
      this.fetchVRActivities();
    },
    createActivity() {
      this.activityBeingEdited = null;
      const nextOrder =
        this.vrActivities.length > 0
          ? Math.max(...this.vrActivities.map((a) => a.sort_order || 0)) + 1
          : 1;
      this.newActivity = {
        name: "",
        sort_order: nextOrder,
        is_active: true,
      };
      this.$modal.open("create_or_edit_activity");
    },
    editActivity(row) {
      this.activityBeingEdited = {
        ...row,
        is_active: !row.archived,
      };
      this.$modal.open("create_or_edit_activity");
    },
    async saveActivity() {
      const activityObj = this.currentActivity;

      if (!activityObj.name || !activityObj.name.trim()) {
        this.$notifyError("Activity name is required!");
        return;
      }

      const payload = {
        name: activityObj.name.trim(),
        sort_order: Number(activityObj.sort_order) || 0,
        archived: !activityObj.is_active,
        service_code: this.selectedActivityServiceCode,
      };

      this.$spinner.show();
      let apiSaveCall;
      if (this.activityBeingEdited) {
        apiSaveCall = vrActivitiesService
          .update(activityObj.id, payload)
          .then(() => this.$notifySuccess("Successfully updated activity."));
      } else {
        apiSaveCall = vrActivitiesService
          .create(payload)
          .then(() => this.$notifySuccess("Successfully created activity."));
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .then(() => this.fetchVRActivities())
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_activity");
          this.activityBeingEdited = null;
        });
    },
    deleteActivity(row) {
      return vrActivitiesService
        .delete(row.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted activity!");
          this.fetchVRActivities();
        })
        .catch((error) => {
          this.$notifyError("Error deleting activity: " + error);
        });
    },
    // Persist the new order after a drag (vuedraggable has already updated
    // vrActivities via v-model). Reload on failure to resync.
    onActivityReorder() {
      const ids = this.vrActivities.map((a) => a.id);
      return vrActivitiesService
        .reorder(ids)
        .then(() => this.$notifySuccess("Order saved."))
        .catch((error) => {
          this.$notifyError("Error saving order: " + error);
          this.fetchVRActivities();
        });
    },

    // --- DSPD Email Notification Settings ---
    async fetchDSPDEmailSettings() {
      this.$spinner.show();
      try {
        if (Object.keys(this.serviceCodeIdMap).length === 0) {
          await this.fetchServiceCodeIds();
        }
        const admins = await adminUsersService.list();
        this.adminUserOptions = admins.map((a) => ({
          id: a.id,
          name: `${a.first_name} ${a.last_name}`,
        }));
        const savedSettings = await dspdEmailSettingsService.list();
        const savedMap = {};
        savedSettings.forEach((s) => {
          savedMap[s.service_code_id] = s.admin_user_ids || [];
        });
        this.dspdEmailSettings = this.dspGroupServiceCodes.map((sc) => ({
          service_code_id: this.serviceCodeIdMap[sc.code],
          code: sc.code,
          name: sc.name,
          admin_user_ids: savedMap[this.serviceCodeIdMap[sc.code]] || [],
        }));
      } catch (error) {
        this.$notifyError("Could not load DSPD email settings: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    async saveDSPDEmailSettings() {
      this.$spinner.show();
      try {
        const payload = this.dspdEmailSettings.map((s) => ({
          service_code_id: s.service_code_id,
          admin_user_ids: s.admin_user_ids,
        }));
        await dspdEmailSettingsService.saveAll(payload);
        this.$notifySuccess("DSPD email settings saved successfully.");
      } catch (error) {
        this.$notifyError("Error saving DSPD email settings: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    // --- Mileage Settings ---
    async fetchMileageSettings() {
      this.$spinner.show();
      try {
        const settings = await mileageSettingsService.get();
        this.mileageSettings = {
          daily_deduction_miles: parseFloat(settings.daily_deduction_miles),
          reimbursement_rate: parseFloat(settings.reimbursement_rate),
          pay_period_cap: parseFloat(settings.pay_period_cap),
          cap_alert_threshold_percent: parseInt(
            settings.cap_alert_threshold_percent,
          ),
        };
      } catch (error) {
        this.$notifyError("Could not load mileage settings: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    async saveMileageSettings() {
      const s = this.mileageSettings;
      if (
        [s.daily_deduction_miles, s.reimbursement_rate, s.pay_period_cap].some(
          (v) => v === null || v === "" || isNaN(parseFloat(v)) || parseFloat(v) < 0,
        )
      ) {
        this.$notifyError("All mileage settings must be non-negative numbers");
        return;
      }
      const threshold = parseInt(s.cap_alert_threshold_percent);
      if (isNaN(threshold) || threshold < 1 || threshold > 100) {
        this.$notifyError("Alert threshold must be between 1 and 100 percent");
        return;
      }
      this.$spinner.show();
      try {
        await mileageSettingsService.save({
          daily_deduction_miles: parseFloat(s.daily_deduction_miles),
          reimbursement_rate: parseFloat(s.reimbursement_rate),
          pay_period_cap: parseFloat(s.pay_period_cap),
          cap_alert_threshold_percent: threshold,
        });
        this.$notifySuccess("Mileage settings saved successfully.");
      } catch (error) {
        this.$notifyError("Error saving mileage settings: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

  },
};
</script>

<style scoped>
.settings-section {
  margin-bottom: 1rem;
}

.vr-activity-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 900px;
}

.vr-activity-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(37, 55, 65, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #e0e0e0;
}

.activity-drag-handle {
  cursor: grab;
  color: #9aa7b0;
}

.activity-drag-handle:active {
  cursor: grabbing;
}

.vr-activity-name {
  flex: 1 1 auto;
  white-space: normal;
  word-break: break-word;
}

.vr-activity-status {
  flex: 0 0 auto;
  font-size: 0.85em;
  min-width: 70px;
  text-align: right;
}

.activity-row-ghost {
  opacity: 0.5;
  background: rgba(64, 158, 255, 0.25);
}

/* Service-code tab strips (question/activity sections). Card tabs get
   scroll arrows from Element UI when they overflow — no wrapping. */
.code-tabs {
  margin-bottom: 0.25rem;
}

.code-tabs :deep(.el-tabs__header) {
  margin-bottom: 0.5rem;
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.code-tabs :deep(.el-tabs__nav) {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.code-tabs :deep(.el-tabs__item) {
  color: #b3a6a6;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.code-tabs :deep(.el-tabs__item.is-active) {
  color: #fff;
  background: rgba(107, 166, 205, 0.18);
}

.code-tabs :deep(.el-tabs__item:hover) {
  color: #fff;
}

.code-tabs :deep(.el-tabs__nav-prev),
.code-tabs :deep(.el-tabs__nav-next) {
  color: #fff;
  line-height: 40px;
}

/* Full name of the selected code, shown under the tab strip */
.code-tabs-caption {
  color: #aaa;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}

/* Mobile swaps the tab strip for a dropdown: with 13+ codes a scrolling
   tab row hides most options, a select shows them all in one tap. */
.code-picker-mobile {
  display: none;
  margin-bottom: 0.75rem;
}

@media (max-width: 768px) {
  .code-tabs {
    display: none;
  }

  .code-picker-mobile {
    display: block;
  }

  /* The dropdown already displays the full code name — the caption
     under the tabs would just repeat it */
  .code-tabs-caption {
    display: none;
  }
}

/* Landing menu: one card per settings section */
.settings-menu {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
  max-width: 960px;
}

.settings-menu-card {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  text-decoration: none;
  transition: all 0.2s ease;
}

.settings-menu-card:hover {
  background: rgba(255, 255, 255, 0.13);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  text-decoration: none;
  transform: translateY(-1px);
}

.settings-menu-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(107, 166, 205, 0.18);
  color: #6ba6cd;
  font-size: 1rem;
}

.settings-menu-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.settings-menu-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.settings-menu-desc {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.settings-menu-chevron {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.8rem;
}

/* Back link shown above every section view */
.settings-back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #6ba6cd;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
}

.settings-back-link:hover {
  color: #8fc1e0;
  text-decoration: none;
}

.settings-content {
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 720px;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  gap: 1rem;
}

.setting-label {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: #fff;
}

.setting-label .text-muted {
  margin-top: 4px;
}

.setting-select {
  flex: 0 0 300px;
  min-width: 0;
}

/* Mobile: label above its select, full-width rows */
@media (max-width: 768px) {
  .settings-content {
    max-width: none;
  }

  .setting-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .setting-select {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
